import { ArrowRight } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { C } from "@/brand/tokens";
import { PHOTOS } from "@/constants/photos";
import { trustAttributes, katrinaBio } from "@/brand/content";

/**
 * TrustSection5 — Soft Parchment institutional trust module.
 *
 * Not a "founder bio block." Reads as institutional proof: portrait
 * matte-framed on the left, a tight authority paragraph, six trust
 * attributes as a disciplined 2×3 grid, and a "brought in when" list
 * that names the exact buyer situations Faith 1st is built for.
 */
export function TrustSection5() {
  return (
    <section
      id="about"
      data-testid="trust"
      style={{
        background: C.surfaceLight2,
        color: C.textOnLight,
      }}
    >
      <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-12 py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-14">
          {/* Portrait — left */}
          <div className="lg:col-span-5">
            <figure className="relative">
              <div
                className="relative p-3"
                style={{
                  background: C.supportOrganic,
                  border: `1px solid ${C.brassSoft}`,
                }}
              >
                <div
                  className="relative overflow-hidden"
                  style={{
                    aspectRatio: "4 / 5",
                    background: C.bgDark,
                  }}
                >
                  <img
                    src={PHOTOS.contemplative}
                    alt="Katrina Bernard, Founder & Principal of Faith 1st"
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{
                      objectPosition: "center 20%",
                      filter: "saturate(0.78) contrast(1.05) brightness(0.96)",
                    }}
                    loading="lazy"
                  />
                </div>
              </div>
              <figcaption className="mt-5 flex items-start justify-between gap-4">
                <div>
                  <p
                    className="font-cormorant"
                    style={{
                      color: C.textOnLight,
                      fontSize: "1.1rem",
                      lineHeight: 1.25,
                    }}
                  >
                    Katrina Bernard, JD&nbsp;|&nbsp;CPC
                  </p>
                  <p
                    className="mt-1.5 font-inter text-[10px] uppercase font-semibold"
                    style={{
                      color: C.accentMetal,
                      letterSpacing: "0.28em",
                    }}
                  >
                    Founder &amp; Principal
                  </p>
                </div>
                <Link
                  to="/concept-e/about"
                  data-testid="founder-bio-link"
                  className="group inline-flex items-center gap-2 pb-1 font-inter text-[10.5px] uppercase font-semibold border-b transition-all"
                  style={{
                    color: C.accentMetal,
                    borderColor: C.accentMetal,
                    letterSpacing: "0.24em",
                  }}
                >
                  Full biography
                  <ArrowRight
                    size={11}
                    weight="bold"
                    className="group-hover:translate-x-1 transition-transform"
                  />
                </Link>
              </figcaption>
            </figure>
          </div>

          {/* Content — right */}
          <div className="lg:col-span-7 lg:pl-6">
            <div className="flex items-center gap-4 mb-7">
              <span
                aria-hidden="true"
                className="inline-block h-px w-14"
                style={{ background: C.accentMetal }}
              />
              <p
                className="font-inter text-[11px] uppercase font-semibold"
                style={{
                  color: C.accentMetal,
                  letterSpacing: "0.28em",
                }}
              >
                Founder authority
              </p>
            </div>
            <h2
              className="font-cormorant"
              style={{
                color: C.textOnLight,
                fontSize: "clamp(2rem, 3.6vw, 3rem)",
                fontWeight: 400,
                lineHeight: 1.06,
                letterSpacing: "-0.012em",
              }}
            >
              Senior judgment, held
              <br />
              with care — brought in
              <br />
              when the room needs it.
            </h2>
            <p
              className="mt-9 font-inter leading-[1.7] max-w-[54ch]"
              style={{ color: C.inkOnLight, fontSize: "1.05rem" }}
            >
              Faith 1st is anchored in two decades of practice at the
              intersection of employment law, senior HR judgment, certified
              executive coaching, and sensitive facilitation. Katrina
              Bernard, JD&nbsp;|&nbsp;CPC, is the founder and principal —
              brought in when leadership transitions, workplace conduct,
              management capability, or People-function pressure need senior
              outside judgment that the internal team can trust to hold with
              care.
            </p>

            {/* Trust attribute grid — 2×3 */}
            <div className="mt-12">
              <p
                className="font-inter text-[10.5px] uppercase font-semibold pb-4 mb-8"
                style={{
                  color: C.accentMetal,
                  letterSpacing: "0.28em",
                  borderBottom: `1px solid ${C.borderSoft}`,
                }}
              >
                Practice signals
              </p>
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-8">
                {trustAttributes.map((a, i) => (
                  <div
                    key={a.label}
                    data-testid={`trust-attribute-${i}`}
                    className="flex items-baseline gap-4"
                  >
                    <span
                      className="font-inter text-[10px] uppercase font-bold tabular-nums shrink-0"
                      style={{
                        color: C.accentMetal,
                        letterSpacing: "0.28em",
                      }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div>
                      <h3
                        className="font-inter text-[12px] uppercase font-semibold mb-1.5"
                        style={{
                          color: C.accentFunctional,
                          letterSpacing: "0.14em",
                        }}
                      >
                        {a.label}
                      </h3>
                      <p
                        className="font-inter text-[13.5px] leading-[1.55]"
                        style={{
                          color: C.inkOnLight,
                        }}
                      >
                        {a.body}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Brought in when — buyer scenarios */}
            <div className="mt-14">
              <p
                className="font-inter text-[10.5px] uppercase font-semibold pb-4 mb-6"
                style={{
                  color: C.accentMetal,
                  letterSpacing: "0.28em",
                  borderBottom: `1px solid ${C.borderSoft}`,
                }}
              >
                Brought in when
              </p>
              <ul className="space-y-3">
                {katrinaBio.broughtInWhen.map((item, i) => (
                  <li
                    key={item}
                    data-testid={`trust-brought-in-${i}`}
                    className="flex items-baseline gap-4"
                  >
                    <span
                      className="font-cormorant tabular-nums shrink-0"
                      style={{
                        color: C.accentMetal,
                        fontSize: "1rem",
                        lineHeight: 1,
                      }}
                    >
                      —
                    </span>
                    <p
                      className="font-inter leading-[1.6]"
                      style={{ color: C.inkOnLight, fontSize: "0.98rem" }}
                    >
                      {item}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
